import { findFieldIndicator } from "@utils/fieldIndicator";

/** Gap between stacked cards. */
export const CARD_SPACING = 12;
/** Height assumed for a card (or the top slot) that hasn't been measured yet. */
export const ESTIMATED_CARD_HEIGHT = 160;

export interface Anchored {
  id: string;
  anchorFieldId: string | null;
  /**
   * Optional fallback DOM row selector value for `[data-suggestion-target-row]`, used when an item
   * has no mounted anchor indicator yet (e.g. a collapsed target row): the card is placed and
   * ordered at the row. Comments never set this — they have no collapsed-row concept.
   */
  rowFallbackId?: string | null;
}

// The item's field, else its collapsed target row, so the card is drawn where the bar steps to it.
// A target field that never renders, even with the row open, keeps its card at the row.
const findAnchorElement = (item: Anchored, fieldId: string, leftContainer: HTMLElement): HTMLElement | null =>
  findFieldIndicator(fieldId) ??
  (item.rowFallbackId
    ? leftContainer.querySelector<HTMLElement>(`[data-suggestion-target-row="${item.rowFallbackId}"]`)
    : null);

/**
 * Calculate raw positions for Anchored items relative to the left container. Ported from
 * Suggestions/helper.tsx's calculateRawPositions — same dual DOM lookup (a mounted
 * `[data-suggestion-indicator]` span, falling back to `getElementById(anchorFieldId)` for
 * anchors that render no indicator span at all, e.g. peer-review comments), then the item's
 * collapsed target row (`rowFallbackId`), and same visibility-window math, generalized to any
 * `anchorFieldId` instead of a suggestion's own field-shape computation.
 */
export const calculateRawPositions = <T extends Anchored>(
  items: T[],
  containerId: string = "AssessmentReport",
): {
  positions: Record<string, number>;
  visibleMap: Record<string, boolean>;
  validItemIds: Set<string>;
} => {
  const leftContainer = document.getElementById(containerId);
  if (!leftContainer) {
    return { positions: {}, visibleMap: {}, validItemIds: new Set() };
  }

  const containerRect = leftContainer.getBoundingClientRect();
  const containerScrollTop = leftContainer.scrollTop;
  const containerScrollBottom = containerScrollTop + leftContainer.clientHeight;

  const positions: Record<string, number> = {};
  const visibleMap: Record<string, boolean> = {};
  const validItemIds = new Set<string>();

  items.forEach((item) => {
    const fieldId = item.anchorFieldId;
    if (!fieldId) return;

    const fieldEl = findAnchorElement(item, fieldId, leftContainer);

    if (!fieldEl) {
      positions[item.id] = 0;
      visibleMap[fieldId] = false;
      return;
    }

    validItemIds.add(item.id);

    const fieldRect = fieldEl.getBoundingClientRect();
    const offsetInsideContainer = fieldRect.top - containerRect.top + containerScrollTop;
    positions[item.id] = Math.round(offsetInsideContainer);

    const fieldTop = offsetInsideContainer;
    const fieldBottom = fieldTop + fieldEl.offsetHeight;
    visibleMap[fieldId] = fieldBottom >= containerScrollTop && fieldTop <= containerScrollBottom;
  });

  return { positions, visibleMap, validItemIds };
};

/**
 * Ported verbatim from Suggestions/helper.tsx's updateLockedOffsets, generalized to key by
 * `anchorFieldId` for any Anchored item instead of only `reportSectionField?.id`. NOTE: as of
 * this port, the resulting lockedOffsets map is not actually read by calculatePositionsForRender
 * below (its `_lockedOffsets` parameter has been unused/underscore-prefixed since before this
 * migration) — this widening from section-fields-only to all anchor types has no observable
 * effect today. Kept because the spec calls out porting this function; do not remove it as part
 * of this migration.
 */
export const updateLockedOffsets = <T extends Anchored>(
  items: T[],
  visibleMap: Record<string, boolean>,
  newPositions: Record<string, number>,
  prevLocked: Record<string, number>,
): Record<string, number> => {
  const next = { ...prevLocked };
  const fieldIds = Array.from(new Set(items.map((item) => item.anchorFieldId ?? "").filter(Boolean)));

  for (const fid of fieldIds) {
    const isVisible = !!visibleMap[fid];

    if (isVisible) {
      const representative = items.find((item) => item.anchorFieldId === fid);
      if (representative) {
        const repPos = newPositions[representative.id] ?? 0;
        if (next[fid] == null || Math.abs(next[fid] - repPos) > 5) {
          next[fid] = repPos;
        }
      }
    }
  }

  return next;
};

/** Ported verbatim from Suggestions/helper.tsx's sortSuggestionsByPosition. */
export const sortItemsByPosition = <T extends Anchored>(items: T[], rawPositions: Record<string, number>): T[] =>
  [...items].sort((a, b) => (rawPositions[a.id] ?? 0) - (rawPositions[b.id] ?? 0));

/**
 * With an active item, it is pinned to its raw position and the cards around it make room. `minTop`
 * reserves the space above it (a slot pinned to the top of the stack): no card starts above it.
 */
export const calculatePositionsForRender = <T extends Anchored>(
  sortedItems: T[],
  rawPositions: Record<string, number>,
  itemHeights: Record<string, number>,
  _lockedOffsets: Record<string, number>,
  activeId: string | null,
  spacing: number = CARD_SPACING,
  estimatedItemHeight: number = ESTIMATED_CARD_HEIGHT,
  minTop: number = 0,
): Record<string, number> => {
  const positions: Record<string, number> = {};
  const heightOf = (item: T) => itemHeights[item.id] || estimatedItemHeight;
  // Each card at its raw top, or just below the card before it if that's lower.
  const stackDown = (items: T[], firstTop: number) => {
    let nextAvailableTop = firstTop;
    for (const item of items) {
      positions[item.id] = Math.max(rawPositions[item.id] ?? 0, nextAvailableTop);
      nextAvailableTop = positions[item.id] + heightOf(item) + spacing;
    }
  };

  const selectedIndex = activeId ? sortedItems.findIndex((item) => item.id === activeId) : -1;

  if (selectedIndex === -1) {
    stackDown(sortedItems, minTop);
    return positions;
  }

  const selectedItem = sortedItems[selectedIndex];
  const selectedRawTop = rawPositions[selectedItem.id] ?? 0;

  // Cards above the pinned one stack as with nothing pinned, then move up only as far as they need
  // to clear the card below. Capping each at its raw top instead would throw a field's earlier
  // cards above it even when the pinned card is far below (PROA-3137).
  stackDown(sortedItems.slice(0, selectedIndex), 0);
  let currentTop = selectedRawTop;
  for (let i = selectedIndex - 1; i >= 0; i--) {
    const item = sortedItems[i];
    positions[item.id] = Math.min(positions[item.id], currentTop - heightOf(item) - spacing);
    currentTop = positions[item.id];
  }

  positions[selectedItem.id] = selectedRawTop;
  stackDown(sortedItems.slice(selectedIndex + 1), selectedRawTop + heightOf(selectedItem) + spacing);

  // The cards above the active one stack upward and can end above minTop; push them, and anything
  // they then overlap, back down below it.
  if (minTop > 0) {
    let nextAvailableTop = minTop;
    for (const item of sortedItems) {
      positions[item.id] = Math.max(positions[item.id], nextAvailableTop);
      nextAvailableTop = positions[item.id] + heightOf(item) + spacing;
    }
  }

  return positions;
};

/** Ported verbatim from Suggestions/helper.tsx's calculateAdjustedTop — no domain typing to begin with. */
export const calculateAdjustedTop = (baseTop: number, scrollOffset: number): number =>
  Math.round(baseTop - scrollOffset);

/** Whether a card at `adjustedTop` is within (a buffer around) the right panel's viewport. */
export const isCardVisibleInRightPanel = (
  adjustedTop: number,
  height: number,
  container: HTMLDivElement | null,
  buffer: number = 40,
): boolean => {
  const containerHeight = container?.clientHeight ?? window.innerHeight;
  const bottom = adjustedTop + height;
  return bottom >= -buffer && adjustedTop <= containerHeight + buffer;
};

export const orderItemsForNavigator = <T extends Anchored>(
  items: T[],
  rawPositions: Record<string, number>,
  validItemIds: Set<string>,
  containerId: string = "AssessmentReport",
): T[] => {
  const leftContainer = document.getElementById(containerId);
  const containerRect = leftContainer?.getBoundingClientRect();
  const containerScrollTop = leftContainer?.scrollTop ?? 0;

  const positionOf = (item: T): number | null => {
    if (validItemIds.has(item.id)) return rawPositions[item.id] ?? 0;
    const rowFallbackId = item.rowFallbackId;
    if (rowFallbackId && leftContainer && containerRect) {
      const rowEl = leftContainer.querySelector(
        `[data-suggestion-target-row="${rowFallbackId}"]`,
      ) as HTMLElement | null;
      if (rowEl) {
        const rect = rowEl.getBoundingClientRect();
        return Math.round(rect.top - containerRect.top + containerScrollTop);
      }
    }
    // calculateRawPositions records 0 for a field it can't find, which is not a real position.
    return null;
  };

  // Items with no position on the page go last, in feed order, rather than ahead of the first field. They stay
  // in the walk: the bar counts them and Complete waits on them, so Next and Resolve must reach them too.
  return [...items]
    .map((item, index) => ({ item, index, pos: positionOf(item) }))
    .sort((a, b) => {
      if (a.pos === null || b.pos === null) return Number(a.pos === null) - Number(b.pos === null) || a.index - b.index;
      return a.pos - b.pos || a.index - b.index;
    })
    .map((entry) => entry.item);
};
